import type { AlertEventStatus, AlertSeverity, AlertType, DispatchStatus, ExpenseStatus, InvoiceStatus, LiveStatus, PositionSource, TripStatus } from './enums.js';

/** Socket.IO namespace used by web + driver app. */
export const WS_NAMESPACE = '/live';

/** Redis pub/sub channel pattern the API subscribes to (one channel per tenant). */
export const rtChannel = (tenantId: string) => `rt:${tenantId}`;

export const wsRooms = {
  tenant: (tenantId: string) => `tenant:${tenantId}`,
  vehicle: (tenantId: string, vehicleId: string) => `tenant:${tenantId}:vehicle:${vehicleId}`,
  user: (userId: string) => `user:${userId}`,
} as const;

/** Client → server */
export interface ClientToServerEvents {
  'subscribe.vehicles': (p: { groupIds?: string[] }) => void;
  'unsubscribe.vehicles': () => void;
  'subscribe.vehicle': (p: { vehicleId: string }) => void;
  'unsubscribe.vehicle': (p: { vehicleId: string }) => void;
}

export interface VehiclePositionEvent {
  vehicleId: string;
  lat: number;
  lon: number;
  speedKph: number | null;
  heading: number | null;
  at: string;
  source: PositionSource;
}

export interface VehicleStateEvent {
  vehicleId: string;
  status: LiveStatus;
  statusSince: string | null;
  activeSource: PositionSource | null;
  ignition: boolean | null;
  odometerKm: number | null;
  fuelLevelPct: number | null;
  address?: string | null;
  placeId?: string | null;
  driverId?: string | null;
  tripId?: string | null;
}

export interface DeviceStatusEvent {
  deviceId: string;
  vehicleId: string | null;
  online: boolean;
  lastSeenAt: string | null;
  extVoltageMv: number | null;
  gsmSignal: number | null;
}

export interface AlertNewEvent {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  status: AlertEventStatus;
  vehicleId: string | null;
  driverId: string | null;
  occurredAt: string;
  lat: number | null;
  lon: number | null;
  titleAr: string;
  titleEn: string;
}

export interface AlertUpdatedEvent {
  id: string;
  status: AlertEventStatus;
  assignedToId: string | null;
}

export interface TripUpdatedEvent {
  tripId: string;
  status: TripStatus;
  dispatchStatus: DispatchStatus;
  vehicleId: string | null;
  driverId: string | null;
}

export interface ExpenseUpdatedEvent {
  expenseId: string;
  status: ExpenseStatus;
  vehicleId: string | null;
  driverId: string | null;
}

export interface InvoiceUpdatedEvent {
  invoiceId: string;
  status: InvoiceStatus;
  customerId: string;
}

export interface NotificationNewEvent {
  id: string;
  titleAr: string;
  titleEn: string;
  bodyAr: string;
  bodyEn: string;
  data: Record<string, unknown>;
  createdAt: string;
}

/** Server → client */
export interface ServerToClientEvents {
  'vehicle.position': (e: VehiclePositionEvent) => void;
  /** Coalesced frame when many vehicles are moving. */
  'vehicle.positions': (e: VehiclePositionEvent[]) => void;
  'vehicle.state': (e: VehicleStateEvent) => void;
  'device.status': (e: DeviceStatusEvent) => void;
  'alert.new': (e: AlertNewEvent) => void;
  'alert.updated': (e: AlertUpdatedEvent) => void;
  'trip.updated': (e: TripUpdatedEvent) => void;
  'expense.updated': (e: ExpenseUpdatedEvent) => void;
  'invoice.updated': (e: InvoiceUpdatedEvent) => void;
  'notification.new': (e: NotificationNewEvent) => void;
  'dispatch.new': (e: TripUpdatedEvent) => void;
}

/** Envelope published on Redis by the worker; the API fans it out to rooms. */
export type RealtimeMessage =
  | { type: 'vehicle.position'; payload: VehiclePositionEvent }
  | { type: 'vehicle.state'; payload: VehicleStateEvent }
  | { type: 'device.status'; payload: DeviceStatusEvent }
  | { type: 'alert.new'; payload: AlertNewEvent }
  | { type: 'alert.updated'; payload: AlertUpdatedEvent }
  | { type: 'trip.updated'; payload: TripUpdatedEvent }
  | { type: 'expense.updated'; payload: ExpenseUpdatedEvent }
  | { type: 'invoice.updated'; payload: InvoiceUpdatedEvent }
  | { type: 'notification.new'; payload: NotificationNewEvent & { userId: string } }
  | { type: 'dispatch.new'; payload: TripUpdatedEvent & { userId: string } };
